import React, { useCallback } from 'react';
import person from '../../../../assets/images/icons/person.png';
import { alpha, Box, Chip, Link } from '@material-ui/core';
import { formatDuration } from './shared/procedureUtils';
import { useStatusColor } from '@/hooks/useStatusColor';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Link as RouterLink, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { useSession, withSession } from '../../../../state/Session';
import { unpackSessionObject } from '../../unpackSessionObject';

const getHospitalIdFromPath = (pathname?: string): number | undefined => {
  const match = pathname?.match(/^\/(?:kiosk|su)\/(\d+)/);
  return match ? Number(match[1]) : undefined;
};

export const PatientName = ({
  id,
  name,
  age,
  sex,
  isPatientIconVisible,
  condensView,
  inline,
  hospitalId,
  isTimeline,
  compactInitials,
  isKiosk: isKioskProp,
  scheduleIsKiosk,
  ...props
}: any) => {
  const classes = useStyles();
  const sessionContext = useSession() as any;
  const isSuperAdmin = props.isSuperAdmin;
  const isGroupAdmin = props.isGroupAdmin;
  // Prefer scheduleIsKiosk so withSession cannot overwrite the schedule/kiosk flag
  const isKiosk = scheduleIsKiosk ?? isKioskProp;
  const ageStyle = age?.endsWith('m') ? { color: '#FF5ABB' } : {};

  const effectiveHospitalId =
    hospitalId ?? getHospitalIdFromPath(props.location?.pathname) ?? props.session?.membership?.organization?.id;

  const patientPath =
    isSuperAdmin || isGroupAdmin
      ? `/su/${effectiveHospitalId}/patients/${id}`
      : `/patients/${id}`;

  const alignMembershipForHospital = useCallback(() => {
    if (isSuperAdmin || isGroupAdmin || !effectiveHospitalId) {
      return;
    }

    const selectMembership = sessionContext?.selectMembership;
    const memberships = sessionContext?.session?.user?.memberships;
    if (!selectMembership || !Array.isArray(memberships) || memberships.length <= 1) {
      return;
    }

    const currentOrgId = sessionContext?.session?.membership?.organization?.id;
    if (String(currentOrgId) === String(effectiveHospitalId)) {
      return;
    }

    const targetMembership = memberships.find(
      (membership: any) => String(membership?.organization?.id) === String(effectiveHospitalId)
    );
    if (targetMembership) {
      selectMembership(targetMembership.id);
    }
  }, [effectiveHospitalId, isGroupAdmin, isSuperAdmin, sessionContext]);

  const handlePatientLinkClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      alignMembershipForHospital();
    },
    [alignMembershipForHospital]
  );

  const patientLink = id && !isKiosk && (
    <Link
      component={RouterLink}
      to={patientPath}
      onClick={handlePatientLinkClick}
      style={{ color: 'inherit' }}
    >
      {isTimeline ? (
        <div style={{ fontSize: '.9rem' }}>
          {name}
          <span style={ageStyle}>{age}</span>
          {sex}
        </div>
      ) : (
        <>
          {name}
          {compactInitials ? '' : ' '}
          <span style={ageStyle}>{age}</span>
          {compactInitials ? '' : ' '}
          {sex}
        </>
      )}
    </Link>
  );

  return (
    <Box
      className={clsx(classes.wrapper, {
        [classes.inline]: inline,
        [classes.condensedView]: condensView,
      })}
    >
      {isPatientIconVisible && (
        <img src={person} alt="icon" style={{ opacity: 0.5, width: '1em', marginRight: '.3333em' }} />
      )}
      {patientLink ? (
        patientLink
      ) : isTimeline ? (
        <div style={{ fontSize: '.9rem' }}>
          {name}
          <span style={ageStyle}>{age}</span>
          {sex}
        </div>
      ) : (
        <>
          {name} <span style={ageStyle}>{age}</span> {sex}
        </>
      )}
    </Box>
  );
};

export const PatientNameWithSession = compose(withRouter, withSession(unpackSessionObject))(PatientName);

const Label = ({ text, backgroundColor }: { text: string; backgroundColor: string }) => (
  <Box borderRadius={4} px={1} my={-1} bgcolor={backgroundColor} style={{ fontWeight: 'bold' }}>
    {text}
  </Box>
);

const Patient = ({
  id,
  name,
  age,
  sex,
  isPatientIconVisible,
  status,
  inOrStatus,
  roomTime,
  bed,
  condensView,
  hospitalId,
  compactInitials,
  isKiosk,
}: any) => {
  const getColor = useStatusColor('schedule');
  const color = getColor(status, hospitalId) || '';
  const classes = useStyles();

  return (
    <Box display="flex" flex="auto">
      <PatientNameWithSession
        id={id}
        condensView={condensView}
        name={name}
        age={age}
        sex={sex}
        isPatientIconVisible={isPatientIconVisible}
        hospitalId={hospitalId}
        compactInitials={compactInitials}
        scheduleIsKiosk={isKiosk}
      />
      <Box
        className={clsx(classes.patientStat, {
          [classes.right]: true,
        })}
      >
        {!!roomTime &&
          formatDuration(roomTime, new Date(), false, {
            fontSize: '0.75rem',
            fontFamily:
              'Rubik,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
          })}{' '}
        <Label backgroundColor={color} text={inOrStatus ? inOrStatus : status} /> {!!bed && <Bed bed={bed} />}
      </Box>{' '}
    </Box>
  );
};

export const Bed = ({ bed }: any) => (
  <Chip
    size="small"
    variant="outlined"
    icon={
      <div
        className="material-icons"
        style={{
          color: 'inherit',
          fontSize: '1.3em',
          marginLeft: '0.4em',
        }}
      >
        hotel{' '}
      </div>
    }
    label={bed}
  />
);

const useStyles = makeStyles(theme => ({
  wrapper: {
    fontSize: '1.16666em',
    backgroundColor: alpha(theme.palette.text.primary, 0.1),
  },
  inline: {
    fontSize: 'inherit',
    padding: 0,
    backgroundColor: 'transparent',
    flex: '0 1 auto',
    whiteSpace: 'nowrap',
  },
  condensedView: {
    flex: 3,
  },
  patientStat: {
    display: 'flex',
    gap: '0.25em',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.text.primary, 0.1),
    flex: '3',
    marginRight: '1px',
    padding: '0.25em 0.375em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  patientStatInline: {
    backgroundColor: 'transparent',
    flex: '0 1 auto',
    padding: '0',
  },
  left: {
    justifyContent: 'flex-start',
  },
  right: {
    justifyContent: 'flex-end',
  },
  center: {
    justifyContent: 'center',
  },
}));

export default Patient;
